import { FC, useEffect, useRef, useState } from 'react';
import './PtaMeetingDetailView.scss';
import images from '../../assets/images';
import { calculateDays, calculateDuration, checkIsTimeNow, formatTime, generateUniqueMeetingId, hasUserAlreadyJoined } from '../../utils/helper';
import { ALERT_MESSAGE_ALREADY_IN_MEETING, ALERT_MESSAGE_TWO_USERS, CHIME, FETCH_POLICY_NO_CACHE, JITSI, PTA_ROLE, SCHOOL_ADMIN, TEACHER_ROLE } from '../../utils/constants';
import { getUserMedia } from '../../utils/webRTCHelper';
import { useAppSelector } from '../../redux/app/hooks';
import { useDispatch } from 'react-redux';
import { setCurrentPtaMeetId, setCurrentPtaMeetMCode, setCurrentSessionDetail, setCurrentSessionRecordingDetail, setCurrentSessionTopic, setMeetComponentName } from '../../redux/slices/dashboardSlice';
import { useGlobalState } from '../../MeetContext/MeetContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CircularProgress, FormControlLabel, FormLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import WarningPopup from '../WarningPopup/WarningPopup';
import { PtaMeetType } from '../../pages/PtaMeetings/PtaMeetings';
import { UPDATE_MEETING_PARTICIPANT_COUNT, UPDATE_MEETING_STATUS } from '../../pages/AdminPta/api/PtaQuery';
import { useChimeMeeting } from '../../Chime/ChimeMeetingContext';
import ToasterWithMessage from '../toaster';
import { trackButtonClick } from '../../firebase/analyticsHelper';
import { ButtonClickEvents } from '../../firebase/constants/events';
import { captureEvent, updateUserProperties } from '../../utils/PostHogHelper';
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import { getChimeParticipants } from '../../Chime/helpers/get-participants';
import { createChimeMeeting } from '../../Chime/helpers/create-meeting';

interface propsType{
    singleSessionDetail: PtaMeetType;
    fetchSessionDetails: () => void;
    sessionEnded: boolean;
    setIsStartingMeeting : (value : boolean) => void;
    isStartingMeeting : boolean;
}

const PtaMeetingDetailView:FC<propsType> = ({ setIsStartingMeeting, isStartingMeeting, singleSessionDetail, fetchSessionDetails, sessionEnded }) => {
  const { calendarSymbol, clockSymbol, sandWatch } = images;
  const [daysleftValue, setDaysleftValue] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('');
  const [isOverflow, setIsOverflow] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  // const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  const [isTeacherButtonClicked, setIsTeacherButtonClicked] = useState<boolean>(false);
  const [isStudentButtonClicked, setIsStudentButtonClicked] = useState<boolean>(false);
  const role = useAppSelector((state) => state.auth.role);
  const loginpage = useAppSelector((state) => state.auth.loginpage);
  const profile = useAppSelector((state) => state?.auth?.adminProfile);
  const dispatch = useDispatch();
  const {localStream, setIsMeetingEndByHost} = useGlobalState();
  const navigate = useNavigate();
  const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
  const location = useLocation();
  const { createMeeting, joinMeeting, setIsRecording, setRecordingStartTime, showChat, setShowChat, setHostId, setHostAttendeeId, setMediaPipelineArn, setMediaPipelineId } = useChimeMeeting();
  const meetService = useAppSelector((state) => state.auth.meetService)
  const { error } = ToasterWithMessage();
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [alreadyTwoUsersSameId, setAlreadyTwoUsersSameId] = useState<boolean>(false)
  const currentUserId = profile?.getCurrentUser?.nodes?.[0]?.uId;
  const isJoined = localStorage.getItem("isJoined") || '';
  const meetingManager = useMeetingManager();
  const REPLICA_THRESHOLD = parseInt(process.env.REACT_APP_REPLICA_THRESHOLD ?? "240");
  const currentPtaMeetId = useAppSelector((state) => state?.dashboard?.currentPtaMeetId);

  const [updateMeetStatus] = useMutation(UPDATE_MEETING_STATUS, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        fetchSessionDetails();
    },
    onError: (err) => {
      setIsStartingMeeting(false);
      // setIsClickedStartButton(false);
      setIsTeacherButtonClicked(false);
    }
  });

  const [updateMeetParticipantCount] = useMutation(UPDATE_MEETING_PARTICIPANT_COUNT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {},
    onError: (err) => {}
  });

  const getPreviewStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: true, audio: true});
      console.log("[Log] stream", stream, stream?.getTracks());
      stream.getTracks().forEach((track) => track.stop());
      return stream;
    } catch (err: any) {
      console.log("[Log] getPreviewStream Error", err);
      let errorMessage = "Unable to access your camera or microphone. Please check your device settings and try again.";
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        errorMessage = "Permission denied. Please allow camera and microphone access.";
      } else if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        errorMessage = "No camera or microphone found. Please check your device.";
      } else if (err?.name === "NotReadableError" || err?.name === "HardwareAccessError") {
        errorMessage = "Another app is using your camera or microphone. Close the other app and try again.";
      } else if (err?.name === "OverconstrainedError") {
        errorMessage = "Your camera does not support the requested video resolution.";
      }
      setWarningMessage(errorMessage);
      if (role === SCHOOL_ADMIN) {
        setIsTeacherButtonClicked(false);
      } else {
        setIsStudentButtonClicked(false);
      }
      setOpenWarningPopup(true);
    }
  };

  const closeWarnPopup = () => {
    setOpenWarningPopup(false);
    setWarningMessage("");
  }
  
  const handleClickStartButton = async () => {
    setIsStartingMeeting(true);
    setIsTeacherButtonClicked(true);
    if (meetService === JITSI) {
      handleStartMeeting();
    } else {
      const stream = await getPreviewStream();
      if (stream) {
        // setIsClickedStartButton(true);
        handleStartMeeting(stream);
      }
    }
  };

  const handleClickJoinButton = async () => {
    setIsStartingMeeting(true);
    if (role === SCHOOL_ADMIN) {
      setIsTeacherButtonClicked(true);
    } else {
      setIsStudentButtonClicked(true);
    }
    if (meetService === JITSI) {
      handleJoinMeeting();
    } else {
      const stream = await getPreviewStream();
      if (stream) {
        handleJoinMeeting(stream);
      }
    }
  };

  const handleStartMeeting = async (stream?: any) => {
    trackButtonClick(ButtonClickEvents?.START_MEETING, {
        description : "User clicked start meeting button",
        page: 'Pta'
    });
    
    dispatch(setMeetComponentName({ meetComponent: 'pta' }));
    if (meetService === JITSI) {
      // Check for Jitsi server url
      if (!singleSessionDetail?.serverUrl) {
        error("Meeting Server setup in progress.");
        setIsTeacherButtonClicked(false);
        return;
      }

      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      const userName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;
      const meetingId = generateUniqueMeetingId();

      try {
        // updateMeetStatus({
        //   variables: {
        //     updateInput: {
        //       mCode: meetingId,
        //       mStatus: "STARTED",
        //       hostId: userId,
        //     },
        //     mId: singleSessionDetail?.mId,
        //   }
        // });
        if (singleSessionDetail?.mId) {
          dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
          dispatch(setCurrentPtaMeetMCode({ currentPtaMeetMCode: singleSessionDetail?.mCode }));
          dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime, mediaPipelineArn : singleSessionDetail?.mediaPipelineArn ?? null , mediaPipelineId : singleSessionDetail?.mediaPipelineId ?? null }));
          dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
          dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
        }
        if(userId) {
              updateUserProperties({
                  meetingId: meetingId,
              });
              captureEvent(userId, {
                  role: role,
                  loginType: loginpage, // your redux login type
                  meetingId: meetingId,
              });
        }
                const existingState = location?.state; // coming from useLocation() or props

                const newState = {
                    ...existingState,                // ✅ spread existing values
                    meetingId,
                    userName,
                    isHost: true,
                    isCreateMeeting: true,
                    hostId: userId,
                    userId,
                    meetComponent: 'pta',
                    jitsiUrls: {
                        jitsiMeetUrl: singleSessionDetail?.serverUrl,
                        jitsiBotUrl: singleSessionDetail?.botUrl
                    }
                };

                // Save to storage
                localStorage.setItem("meetingState", JSON.stringify(newState));
                localStorage.setItem("isJoined", "joined");

                // Open in new tab
                if(window?.electron && window?.electron?.isElectron()){
                    window?.electron?.openMeetingWindow(`admin/pta/meeting?meetId=${meetingId}`, newState);
                }else {
                    window.open(`/admin/pta/meeting?meetId=${meetingId}`, "_blank");
                }        
        // navigate(`/admin/pta/meeting?meetId=${meetingId}`, {
        //   state: { ...location?.state, meetingId: meetingId, userName: userName, isHost: true, isCreateMeeting: true, hostId: userId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
        // });
      }
      catch (err) {
        localStorage.setItem("isJoined", "left");
        console.log("Creating Meeting Error :", err);
        setIsTeacherButtonClicked(false);
      }
    }
    else {
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      await createMeeting(userId).then(async (response) => {
        console.log('[Log] createMeeting response', response);
        const meetId = response?.Meeting?.MeetingId;
        // const data = await response.json();
        if (meetId) {
          // const {meetId, user, host, createdAt} = data.meetInfo;
          try {
            // await InitialiseMeeting({
            //   meetId,
            //   avatar: getRandomColor(),
            //   currentUser: {
            //     userId: user.userId,
            //     avatarKey: user.avatarKey,
            //       userKey: user.userKey,
            //       userName: user.userName,
            //       preference: {
            //           audio: true,
            //           video: true,
            //           screen: false,
            //           hand: false
            //       },
            //       isPinned: {video: false, screen: false}
            //   },
            //   host:{
            //       hostId: host.hostId,
            //       hostKey: host.hostKey,
            //       hostName: host.hostName
            //   },
            //   createdAt
            // }, FormType.CREATE);
            // await updateMeetParticipantCount({
            //   variables: {
            //     input: {
            //       mId: singleSessionDetail?.mId,
            //       participantType: true
            //     }
            //   }
            // });
          } catch (err) {
            setIsStartingMeeting(false);
            setIsTeacherButtonClicked(false);
          }

          const participants = await getChimeParticipants(meetId);
          let hostAttendee: any = null;
          // Find the host attendee
          if (participants?.Attendees) {
              hostAttendee = participants.Attendees.find(
                  (attendee) => attendee.ExternalUserId === userId
              );
          }

          console.log(
              "Host AttendeeId while creating:",
              hostAttendee?.AttendeeId
          );
          localStorage.setItem("hostId", userId ?? "");
          localStorage.setItem(
              "hostAttendeeId",
              hostAttendee?.AttendeeId ?? ""
          );
          setHostAttendeeId(hostAttendee?.AttendeeId || "");
          updateMeetStatus({
            variables: {
              updateInput: {
                mCode: meetId,
                mStatus: "STARTED",
                hostId: userId,
                showChat: singleSessionDetail?.mStatus === "NOT_STARTED" ? showChat : singleSessionDetail?.showChat,
                originalHostId : userId
              },
              mId: singleSessionDetail?.mId,
            }
          });
          if (singleSessionDetail?.mId) {
            if (singleSessionDetail?.mStatus === "STARTED") {
              setShowChat(singleSessionDetail?.showChat);
            }
            dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
            dispatch(setCurrentPtaMeetMCode({ currentPtaMeetMCode: meetId }));
            dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime, mediaPipelineArn : singleSessionDetail?.mediaPipelineArn ?? null , mediaPipelineId : singleSessionDetail?.mediaPipelineId ?? null }));
            dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
            dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
          }
          if(userId) {
              updateUserProperties({
                  meetingId: meetId,
              });
              captureEvent(userId, {
                  role: role,
                  loginType: loginpage, // your redux login type
                  meetingId: meetId,
              });
        }
          navigate(`/admin/pta/meeting?meetId=${meetId}`, {
            state: { ...location?.state, sessionId: singleSessionDetail?.mId }
          });
        }
      }).catch((error) => {
        console.log("Creating Meeting Error :", error);
        setIsStartingMeeting(false);
        setIsTeacherButtonClicked(false);
        stream.getAudioTracks()[0].stop();
        stream.getVideoTracks()[0].stop();
      });
    }
  };
  
  const handleJoinMeeting = async (stream?: any) => {
    trackButtonClick(ButtonClickEvents?.JOIN_MEETING, {
        description : "User clicked join meeting button",
        page: 'Pta'
    });

    dispatch(setMeetComponentName({ meetComponent: 'pta' }));
    if (meetService === JITSI) {
      // Check for Jitsi server url
      if (!singleSessionDetail?.serverUrl) {
        error("Meeting Server setup in progress.");
        if (role === SCHOOL_ADMIN) {
          setIsTeacherButtonClicked(false);
        } else {
          setIsStudentButtonClicked(false);
        }
        return;
      }
      
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      const userName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;

      if (singleSessionDetail?.mCode && singleSessionDetail?.hostId) {
        try {
          if (singleSessionDetail?.mId) {
            dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
            dispatch(setCurrentPtaMeetMCode({ currentPtaMeetMCode: singleSessionDetail?.mCode }));
            dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime, mediaPipelineArn : singleSessionDetail?.mediaPipelineArn ?? null , mediaPipelineId : singleSessionDetail?.mediaPipelineId ?? null }));
            dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
            setRecordingStartTime(singleSessionDetail?.recordStartTime);
            setIsRecording(singleSessionDetail?.isRecordStart);
            dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
          }
          if(userId) {
                    updateUserProperties({
                         meetingId: singleSessionDetail?.mCode, // overwrite old value
                    });
                    captureEvent(userId, {
                        role: role,
                        loginType: loginpage, // your redux login type
                        meetingId: singleSessionDetail?.mCode,
                    });
          }
          const existingState = location?.state; // coming from useLocation() or props
          localStorage.setItem("isJoined", "joined");
          if (role === PTA_ROLE) {
              const newState = {
                ...existingState,   
                meetingId: singleSessionDetail?.mCode, 
                userName: userName, 
                isHost: false, 
                isCreateMeeting: false, 
                hostId: singleSessionDetail?.hostId, 
                userId, 
                meetComponent: 'pta', 
                jitsiUrls: { 
                    jitsiMeetUrl: singleSessionDetail?.serverUrl, 
                    jitsiBotUrl: singleSessionDetail?.botUrl 
                }
                };

                // Save to storage
                localStorage.setItem("meetingState", JSON.stringify(newState));

                if(window?.electron && window?.electron?.isElectron()){
                    window?.electron?.openMeetingWindow(`pta/meeting?meetId=${singleSessionDetail?.mCode}`, newState);
                } else {
                   window.open(`/pta/meeting?meetId=${singleSessionDetail?.mCode}`, "_blank");
                }            
            // navigate(`/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
            //   state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: false, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }

            // });
          }
          else if (role === SCHOOL_ADMIN) {
            const newState = {
                ...existingState,   
                meetingId: singleSessionDetail?.mCode, 
                userName: userName, 
                isHost: singleSessionDetail?.hostId && userId === singleSessionDetail.hostId, 
                isCreateMeeting: false, 
                hostId: singleSessionDetail?.hostId, 
                userId, 
                meetComponent: 'pta', 
                jitsiUrls: { 
                    jitsiMeetUrl: singleSessionDetail?.serverUrl, 
                    jitsiBotUrl: singleSessionDetail?.botUrl 
                }
                };

                // Save to storage
                localStorage.setItem("meetingState", JSON.stringify(newState));

                if(window?.electron && window?.electron?.isElectron()){
                    window?.electron?.openMeetingWindow(`admin/pta/meeting?meetId=${singleSessionDetail?.mCode}`, newState);
                } else {
                   window.open(`/admin/pta/meeting?meetId=${singleSessionDetail?.mCode}`, "_blank");
                }   

            // navigate(`/admin/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
            //   state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: singleSessionDetail?.hostId && userId === singleSessionDetail.hostId, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
            // });
          }
          else if (role === TEACHER_ROLE) {
            const newState = {
                ...existingState,   
                meetingId: singleSessionDetail?.mCode, 
                userName: userName, 
                isHost: false, 
                isCreateMeeting: false, 
                hostId: singleSessionDetail?.hostId, 
                userId, 
                meetComponent: 'pta', 
                jitsiUrls: { 
                    jitsiMeetUrl: singleSessionDetail?.serverUrl, 
                    jitsiBotUrl: singleSessionDetail?.botUrl 
                }
                };

                // Save to storage
                localStorage.setItem("meetingState", JSON.stringify(newState));

                if(window?.electron && window?.electron?.isElectron()){
                    window?.electron?.openMeetingWindow(`teacher/pta/meeting?meetId=${singleSessionDetail?.mCode}`, newState);
                } else {
                   window.open(`/teacher/pta/meeting?meetId=${singleSessionDetail?.mCode}`, "_blank");
                } 

            // navigate(`/teacher/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
            //   state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: false, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
            // });
          }
        }
        catch (e) {
          localStorage.setItem("isJoined", "left");
          if (role === SCHOOL_ADMIN) {
            setIsTeacherButtonClicked(false);
          } else {
            setIsStudentButtonClicked(false);
          }
          console.log("[Log] Error", e);
        }
      }
    }
    else {
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      if (singleSessionDetail?.mCode && singleSessionDetail?.hostId) {
        let primaryMeetingId = singleSessionDetail?.mCode;
        const replicaMeetingIds: string[] =
            singleSessionDetail?.replicaMeetingIds || [];

        // Combine the primary meeting and any replica meetings into one list
        const allMeetingIds = [primaryMeetingId, ...replicaMeetingIds];
        console.log("All Meeting Ids:", allMeetingIds);

        // Fetch participant counts for all meeting IDs in parallel
        const meetingCountsPromises = allMeetingIds.map(async (id) => {
            const response = await getChimeParticipants(id);
            const count = response?.Attendees ? response?.Attendees?.length : 0;
            return { id, count };
        });
        const meetingCounts = await Promise.all(meetingCountsPromises);
        console.log("Meeting Counts:", meetingCounts);

        // Decide which meeting instance to join: choose the first meeting that isn't full.
        let chosenMeetingId: string | null = null;
        for (const { id, count } of meetingCounts) {
            if (count < REPLICA_THRESHOLD) {
                chosenMeetingId = id;
                break;
            }
        }

        // If all existing meeting instances are full, create a new replica meeting.
        if (!chosenMeetingId) {
            const replicaMeetingResponse = await createChimeMeeting(
                singleSessionDetail?.mCode
            );
            const newReplicaMeetingId =
                replicaMeetingResponse?.Meeting?.MeetingId;
            if (newReplicaMeetingId) {
                // Update the replica meetings array by appending the new meeting ID.
                const updatedReplicaMeetingIds = [
                    ...replicaMeetingIds,
                    newReplicaMeetingId,
                ];
                updateMeetStatus({
                  variables: {
                    updateInput: {
                      replicaMeetingIds: updatedReplicaMeetingIds,
                    },
                    mId: currentPtaMeetId,
                  }
                });
                chosenMeetingId = newReplicaMeetingId;
            }
        }

        // Fallback: if still no meeting is chosen, default to primary meeting
        const finalMeetingId = chosenMeetingId || primaryMeetingId;
        const originalHostId = singleSessionDetail.originalHostId;

        let hostAttendee: any = null;
        let hostExternalUserId: any = "";

        // Assume you already have these
        await getChimeParticipants(finalMeetingId)
            .then((participants) => {
                console.log("[Log] Participants List:", participants);
                console.log(
                    "userId",
                    userId,
                    "singleSessionDetail.hostId",
                    singleSessionDetail.hostId,
                    "originalHostId",
                    originalHostId
                );
                if (userId === originalHostId) {
                    hostExternalUserId = userId;
                } else {
                    hostExternalUserId = singleSessionDetail.hostId;
                }
                // Find the host attendee
                if (participants?.Attendees) {
                    console.log(
                        "by using this hostExternalUserId",
                        hostExternalUserId
                    );
                    hostAttendee = participants.Attendees.find(
                        (attendee: any) => {
                            console.log(
                                "Checking attendee:",
                                attendee.ExternalUserId
                            );
                            return (
                                attendee.ExternalUserId === hostExternalUserId
                            );
                        }
                    );
                }
            })
            .catch((error) => {
                console.error("Failed to fetch participants:", error);
            });

        if (hostAttendee) {
            console.log("Host AttendeeId:", hostAttendee.AttendeeId);
        } else {
            console.log("Host not found in the participants list");
        }     
        await joinMeeting(
          singleSessionDetail.mCode,
          (originalHostId === userId) ? userId :
              (singleSessionDetail.hostId === userId)
              ? singleSessionDetail.hostId
              : `${userId}|${Date.now()}`, 
          singleSessionDetail.hostId, 
          originalHostId, hostAttendee?.AttendeeId || '')
          .then(async (response) => {
            console.log('[Log] joinMeeting response', response);
            const meetId = response?.meetingResponse?.Meeting?.MeetingId;
            // const data = await response.json();
            if (meetId) {
              // const {meetId, user, host, createdAt} = data.meetInfo;
              if (userId === originalHostId) {
                  await getChimeParticipants(meetId)
                      .then((participantsResponse) => {
                          console.log(
                              "userId",
                              userId,
                              "singleSessionDetail.hostId",
                              singleSessionDetail.hostId,
                              "originalHostId",
                              originalHostId
                          );
                          hostExternalUserId = userId;
                          // Find the host attendee
                          if (participantsResponse?.Attendees) {
                              hostAttendee =
                                  participantsResponse.Attendees.find(
                                      (attendee: any) => {
                                          console.log(
                                              "Checking attendee:",
                                              attendee.ExternalUserId
                                          );
                                          return (
                                              attendee.ExternalUserId ===
                                              hostExternalUserId
                                          );
                                      }
                                  );
                              console.log(
                                  "Host AttendeeId after joining:",
                                  hostAttendee?.AttendeeId || ""
                              );
                              setHostAttendeeId(hostAttendee?.AttendeeId || "");
                              console.log(
                                  singleSessionDetail.mediaPipelineArn,
                                  singleSessionDetail.mediaPipelineId,
                                  "mediapipeline",
                                  originalHostId
                              );
                              localStorage.setItem("hostId", userId ?? "");
                              localStorage.setItem(
                                  "hostAttendeeId",
                                  hostAttendee?.AttendeeId ?? ""
                              );
                          }
                      })
                      .catch((error) => {
                          console.error("Failed to fetch participants:", error);
                      });

                  console.log(userId, originalHostId, "originalHost");
                  setHostId(userId);
                  console.log(
                      singleSessionDetail.mediaPipelineArn,
                      singleSessionDetail.mediaPipelineId,
                      "mediapipeline",
                      originalHostId
                  );

                  setMediaPipelineArn(singleSessionDetail.mediaPipelineArn);
                  setMediaPipelineId(singleSessionDetail.mediaPipelineId);
                  meetingManager.audioVideo?.addObserver({
                      audioVideoDidStart: () => {
                          console.log(
                              "[Log] audioVideoDidStart - Sending realtime message"
                          );
                          try {
                              const message = {
                                  action: "host-changed",
                                  hostId: userId,
                                  hostAttendeeId:
                                      hostAttendee?.AttendeeId || "",
                                  ...(singleSessionDetail.mediaPipelineId && {
                                      mediaPipelineId:
                                          singleSessionDetail.mediaPipelineId,
                                  }),
                                  ...(singleSessionDetail.mediaPipelineArn && {
                                      mediaPipelineArn:
                                          singleSessionDetail.mediaPipelineArn,
                                  }),
                              };

                              meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(
                                  "live-update",
                                  JSON.stringify(message)
                              );
                          } catch (error) {
                              console.error(
                                  "Failed to send realtime message:",
                                  error
                              );
                          }
                      },
                  });

                  await updateMeetStatus({
                    variables: {
                      updateInput: {
                        mCode: singleSessionDetail?.mCode,
                        hostId: userId,
                      },
                      mId: currentPtaMeetId,
                    }
                  })
              }
              try {
                //   await InitialiseMeeting({
                //     meetId,
                //     avatar: getRandomColor(),
                //     currentUser: {
                //       userId: user.userId,
                //       avatarKey: user.avatarKey,
                //         userKey: user.userKey,
                //         userName: user.userName,
                //         preference: {
                //             audio: true,
                //             video: true,
                //             screen: false,
                //             hand: false
                //         },
                //         isPinned: {video: false, screen: false}
                //     },
                //     host:{
                //         hostId: host.hostId,
                //         hostKey: host.hostKey,
                //         hostName: host.hostName
                //     },
                //     createdAt
                // }, FormType.JOIN);
                // await updateMeetParticipantCount({
                //   variables: {
                //     input: {
                //       mId: singleSessionDetail?.mId,
                //       participantType: true
                //     }
                //   }
                // })
              } catch (err) {
                setIsStartingMeeting(false);
                if (role === SCHOOL_ADMIN) {
                  setIsTeacherButtonClicked(false);
                } else {
                  setIsStudentButtonClicked(false);
                }
              }
              if (singleSessionDetail?.mId) {
                dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
                dispatch(setCurrentPtaMeetMCode({ currentPtaMeetMCode: singleSessionDetail?.mCode }));
                dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime, mediaPipelineArn : singleSessionDetail?.mediaPipelineArn ?? null , mediaPipelineId : singleSessionDetail?.mediaPipelineId ?? null }));
                dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
                setRecordingStartTime(singleSessionDetail?.recordStartTime);
                setIsRecording(singleSessionDetail?.isRecordStart);
                dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
                setShowChat(singleSessionDetail?.showChat);
              }
              if(userId) {
                    updateUserProperties({
                         meetingId: meetId, // overwrite old value
                    });
                    captureEvent(userId, {
                        role: role,
                        loginType: loginpage, // your redux login type
                        meetingId: meetId,
                    });
              }
              if (role === PTA_ROLE) {
                navigate(`/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
              else if (role === SCHOOL_ADMIN) {
                navigate(`/admin/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
              else if (role === TEACHER_ROLE) {
                navigate(`/teacher/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
            }
          }).catch(() => {
            setIsStartingMeeting(false);
            if (role === SCHOOL_ADMIN) {
              setIsTeacherButtonClicked(false);
            } else {
              setIsStudentButtonClicked(false);
            }
            stream.getAudioTracks()[0].stop();
            stream.getVideoTracks()[0].stop();
          })
      }
    } 
  };

  //To check the container is overflow
  const checkOverflow = () => {
    if (descriptionRef.current) {
      setIsOverflow(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
    }
  };


  useEffect(() => {
    if(singleSessionDetail){
      const daysLeft = calculateDays(singleSessionDetail?.mStartTime, true);
      const startTimeFormatted = formatTime(singleSessionDetail?.mStartTime);
      const durationFormatted = calculateDuration(singleSessionDetail?.mStartTime, singleSessionDetail?.mEndTime);

      setDaysleftValue(daysLeft);
      setStartTime(startTimeFormatted);
      setDuration(durationFormatted);
      checkOverflow();
      const interval = setInterval(() => {
        fetchSessionDetails();
      }, 15000); // 60000 ms = 1 minute

      return () => clearInterval(interval);
    }
  },[singleSessionDetail])

  useEffect(() => {
    dispatch(setCurrentSessionDetail({startDate: null, endDate: null, mediaPipelineArn: null, mediaPipelineId: null}));
    dispatch(setCurrentSessionRecordingDetail({recordingStartedTime: null, recordingStarted: false}));
    setIsMeetingEndByHost(false);
      //To set stop camera and microphone when initial render.
      if(localStream?.getVideoTracks()[0]){
            localStream.getVideoTracks()[0].enabled = false;
        }
        localStream?.getVideoTracks()[0]?.stop();
        if(localStream?.getAudioTracks()[0]){
            localStream.getAudioTracks()[0].enabled = false;
        }
        localStream?.getAudioTracks()[0]?.stop();
        setIsTeacherButtonClicked(false);
        setIsStudentButtonClicked(false);
        localStorage.removeItem("hostId");
        localStorage.removeItem("hostAttendeeId");
  }, [])

  const isSessionStarted = singleSessionDetail?.mStatus === 'STARTED' ? true : false;
  const StudentJoinButtonCondition = (isSessionStarted && !sessionEnded && (singleSessionDetail?.mParticipantCount > 0));

  const isServerUrlReady = !singleSessionDetail?.meetingType || (singleSessionDetail?.meetingType === "JITSI" && singleSessionDetail?.serverUrl);

  useEffect(()=>{
      const alreadyJoined = hasUserAlreadyJoined(singleSessionDetail?.participantIds || [], currentUserId);
      setAlreadyTwoUsersSameId(alreadyJoined)
  
  },[singleSessionDetail?.participantIds])

  useEffect(()=>{
        if(sessionEnded) {
        setAlreadyTwoUsersSameId(false)
        }
      },[sessionEnded])

  return (
    <div className='singleSessionDetail'>
      <div className='leftSideSessionDetail'>
          <div className='sesstionTitle'>MEETING</div>
          <div className='listOfSessionDetail'>
            <div className='listsStyle'>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={calendarSymbol} alt='calendarIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{daysleftValue}</div>
                </div>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={clockSymbol} alt='clockIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{startTime}</div>
                </div>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={sandWatch} alt='sandIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{duration}</div>
                </div>
            </div>
          </div>
      </div>
      <div className='rightSideSessionDetail' style={(meetService === CHIME) ? {gap: '5px'} : undefined}>
          <div className='lessonTopicContainer'>
            <div className='heaaderStyle'>
              <div className='headTextStyle'>MEETING TOPIC:</div>
            </div>
            <Tooltip title={singleSessionDetail?.mTopic} disableHoverListener={!isOverflow}>
              <div
                className='descriptionStyle'
                ref={descriptionRef}
                onMouseEnter={checkOverflow}
                >
                {singleSessionDetail?.mTopic}
              </div>
            </Tooltip>
          </div>
          { (role === SCHOOL_ADMIN) ? (
            <>
              { singleSessionDetail?.mStatus === "NOT_STARTED" && (role === SCHOOL_ADMIN) && <div className='radioContainer'>
                  <FormLabel sx={{ color: "black", fontFamily: "var(--font-inter)", fontSize: "14px" }} component="legend">Chat :</FormLabel>
                      <RadioGroup
                        row
                        name="chat-options"
                        value={showChat}
                        onChange={() => setShowChat(!showChat)}
                      >
                        <FormControlLabel sx={{
                          '& .MuiFormControlLabel-label': {
                              color: 'black',
                              fontFamily: 'var(--font-inter)',
                              fontSize: '14px',
                          },
                          }} 
                          value={true} 
                          control={<Radio sx={{
                                  transform: 'scale(0.8)', // scale down the radio circle
                                  padding: '4px',          // reduce padding if needed
                                  }} />} label="Enable" />
                          <FormControlLabel sx={{
                              '& .MuiFormControlLabel-label': {
                                  color: 'black',
                                  fontFamily: 'var(--font-inter)',
                                  fontSize: '14px',
                              },
                          }} 
                          value={false} 
                          control={<Radio sx={{
                              transform: 'scale(0.8)', // scale down the radio circle
                              padding: '4px',          // reduce padding if needed
                          }} />} label="Disable" />
                          </RadioGroup>
              </div>}            
            <div><button 
              className={`${(singleSessionDetail?.isActive && !sessionEnded && !isTeacherButtonClicked && isServerUrlReady && ((isJoined === "left" || isJoined === ''))) ? 'sessionStartButton' : 'notReadyButtonStyle'} ${(isSessionStarted && isServerUrlReady) ? 'sessionStartButton' : ''}`} 
              onClick={
                meetService === JITSI ?
                ((isSessionStarted) ? handleClickJoinButton : handleClickStartButton)
                : ((isSessionStarted && singleSessionDetail?.mParticipantCount === 0) ? handleClickStartButton : (isSessionStarted && singleSessionDetail?.mParticipantCount > 0) ? handleClickJoinButton : handleClickStartButton)
              } 
              disabled={isStartingMeeting}
            > 
              {isStartingMeeting ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress size={15} color="inherit" />
                      </div>
              ) :
                sessionEnded ? `Session Ended` : (singleSessionDetail?.isActive && !isSessionStarted && isServerUrlReady) ? `Start` :(isSessionStarted && isServerUrlReady)? 'Join' : 'Not Ready'
              }
            </button>
            {(!(isJoined === "left" || isJoined === '')) && singleSessionDetail?.isActive && !sessionEnded && isServerUrlReady && (
                    <span className='alreadyInMeeting'>
                        {ALERT_MESSAGE_ALREADY_IN_MEETING}
                    </span>
            )}
            </div>
            </>
            ) : (
            <div>
              <button 
                className={`${(StudentJoinButtonCondition && !isStudentButtonClicked && !alreadyTwoUsersSameId && ((isJoined === "left" || isJoined === ''))) ? 'sessionJoinButton' : 'notReadyStudentButtonStyle'}`} 
                onClick={handleClickJoinButton}
              >
                {
                    (sessionEnded ? `Session Ended` : (isSessionStarted && singleSessionDetail?.mParticipantCount === 0) ? `Session completed` : isSessionStarted ? `Join` : 'Not Ready')
                }
              </button>
              {(!isSessionStarted && !sessionEnded && singleSessionDetail?.isActive) && (
                <span className='sessionNotYetStarted'>
                  Host has not yet started the meeting
                </span>
              )}
            {alreadyTwoUsersSameId && (
            <span className='alert-msg'>
               {ALERT_MESSAGE_TWO_USERS}
            </span>
          )}
            {(!(isJoined === "left" || isJoined === '')) && StudentJoinButtonCondition && !alreadyTwoUsersSameId && !sessionEnded && (
                    <span className='alreadyInMeeting'>
                        {ALERT_MESSAGE_ALREADY_IN_MEETING}
                    </span>
            )}
            </div>)
          }
      </div>
      <WarningPopup
        message={warningMessage}
        openWarningPopup={ openWarningPopup }
        closeWarnPopup={ closeWarnPopup }
      />
    </div>
  )
}

export default PtaMeetingDetailView;
